import { useRef, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useGetPatients } from '../../hooks/patientsAPI';
import {
  ExistingPatient,
  PatientSearchProperties,
  PatientSortColumn,
  ProfileFilterState,
  SearchPatientsParams,
  SortOrder,
} from '../../types';
import { DataTable, Pagination, PaginationState } from '@/components';
import { SortingState, OnChangeFn } from '@tanstack/react-table';
import { useDebounce } from '@/hooks';
import { EditPatientModal } from '../EditPatientModal';
import { SearchFilterHeader } from './SearchFilterHeader';
import { createColumns } from './tableColumns';
import PatientRequestsAccordion from './PatientRequestsAccordion';
import {
  applyProfileFilter,
  getProfileFilterSearchParams,
} from './profileFilter';

// Empty State Component
const EmptyStateComponent = () => (
  <div className="flex items-center justify-center py-12">
    <div className="text-center">
      <img
        src="/suggested-providers.png"
        alt="No patients found"
        className="mx-auto mb-8 max-w-md"
      />
      <h2 className="text-2xl font-bold">No Patients Found</h2>
      <p className="text-gray-600">Try adjusting your search or filters.</p>
    </div>
  </div>
);

const DEFAULT_SORT: Array<{ column: PatientSortColumn; order: SortOrder }> = [
  { column: 'updatedAt', order: 'desc' },
];

export const PatientsList = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 10,
  });
  const [editingPatient, setEditingPatient] = useState<ExistingPatient | null>(
    null,
  );
  const profileFilterOpenRef = useRef(false);

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'updatedAt', desc: true },
  ]);
  const [search, setSearch] = useState<PatientSearchProperties>({
    terms: [],
    dateOfBirth: undefined,
    statuses: [],
    profileFilterMode: 'exclude',
    excludeOrganizationProfileIds: [],
    modifiedFrom: undefined,
    modifiedTo: undefined,
  });
  const debouncedSearchTerm = useDebounce(search.terms[0] || '', 300);

  const profileFilterSearchParams = useMemo(
    () => getProfileFilterSearchParams(search),
    [search],
  );

  // Convert TanStack sorting state to API format
  const sortColumns = useMemo(() => {
    if (sorting.length === 0) return DEFAULT_SORT;

    const order = (desc: boolean) =>
      desc ? ('desc' as const) : ('asc' as const);

    return sorting.map((sort) => ({
      column: sort.id as PatientSortColumn,
      order: order(sort.desc),
    }));
  }, [sorting]);

  const searchParams: SearchPatientsParams['search'] = {
    terms: debouncedSearchTerm ? [debouncedSearchTerm] : [],
    ...(search.dateOfBirth && {
      dateOfBirth: search.dateOfBirth,
    }),
    ...(search.statuses.length > 0 && {
      statuses: search.statuses,
    }),
    ...profileFilterSearchParams,
    ...(search.modifiedFrom && {
      modifiedFrom: format(search.modifiedFrom, 'yyyy-MM-dd'),
    }),
    ...(search.modifiedTo && {
      modifiedTo: format(search.modifiedTo, 'yyyy-MM-dd'),
    }),
  };

  const { data: patientsData, isFetching } = useGetPatients({
    search: searchParams,
    pagination: {
      offset: pagination.page * pagination.pageSize,
      limit: pagination.pageSize,
    },
    sortColumns,
    includeRequests: true,
  });

  const handleEditPatient = (patient: ExistingPatient) => {
    setEditingPatient(patient);
  };

  const handleCloseEditModal = () => {
    setEditingPatient(null);
  };

  const handleSearchChange = (newSearch: PatientSearchProperties) => {
    // Reset to first page when search terms change
    if (newSearch.terms[0] !== search.terms[0]) {
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
    setSearch(newSearch);
  };

  const handleProfileFilterChange = (profileFilter: ProfileFilterState) => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setSearch((prevSearch) => applyProfileFilter(prevSearch, profileFilter));
  };

  const columns = createColumns(
    search,
    handleProfileFilterChange,
    handleEditPatient,
    profileFilterOpenRef,
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SearchFilterHeader
        search={search}
        handleSearchChange={handleSearchChange}
      />
      <div className="px-6 pb-6">
        <DataTable
          columns={columns}
          data={patientsData?.patients || []}
          isLoading={isFetching}
          pageSize={pagination.pageSize}
          sorting={sorting}
          onSortingChange={setSorting as OnChangeFn<SortingState>}
          emptyState={<EmptyStateComponent />}
          accordionContent={(patient: ExistingPatient) => (
            <PatientRequestsAccordion patient={patient} />
          )}
        />
        <Pagination
          pagination={pagination}
          setPagination={setPagination}
          totalCount={patientsData?.totalCount ?? 0}
          pageSizeOptions={[10, 20, 50]}
        />
      </div>
      {editingPatient && (
        <EditPatientModal
          patient={editingPatient}
          isOpen={!!editingPatient}
          onClose={handleCloseEditModal}
        />
      )}
    </div>
  );
};
